import React, { useEffect, useRef, useState } from "react";
import { CameraIcon, Plus } from "./icons";
import styles from "../publicvehicle.module.css";

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const VALID_EXTENSIONS = ["jpg", "jpeg", "png"];
const COLORS = [
  { id: "Black", hex: "#1B1B1F" },
  { id: "White", hex: "#FFFFFF" },
  { id: "Silver", hex: "#C8CBD0" },
  { id: "Gray", hex: "#7A7D85" },
  { id: "Blue", hex: "#1F4FD1" },
  { id: "Red", hex: "#C62828" },
  { id: "Green", hex: "#2E7D4F" },
];
const SLOT_LABELS = ["Front", "Rear + plate"];

// The color text box is the value; a tile is highlighted when the text matches it
const isColor = (value, id) => value.trim().toLowerCase() === id.toLowerCase();

/*
 * Vehicle number / make / model / color plus photos, used by the tenant and guest pages.
 * requirePhotos: at least minPhotos are needed (new vehicles, guests). For updates photos are optional, but if
 * any are added there must be at least minPhotos (they replace the current ones).
 * onSubmit(values, photos) must return a promise; the form is disabled while `submitting` is true.
 */
const VehicleDetailsForm = ({ defaultValues, requirePhotos, minPhotos = 2, maxPhotos = 6, submitting, submitLabel = "SUBMIT VEHICLE", compact = false, onSubmit, onCancel }) => {
  const [license, setLicense] = useState(defaultValues?.license || "");
  const [make, setMake] = useState(defaultValues?.make || "");
  const [model, setModel] = useState(defaultValues?.model || "");
  const [color, setColor] = useState(defaultValues?.color || "");
  const [photos, setPhotos] = useState([]);
  const [photoError, setPhotoError] = useState("");
  const [errors, setErrors] = useState({});
  const summaryRef = useRef(null);
  const [failedSubmits, setFailedSubmits] = useState(0);

  // Bring the error summary into view after a failed submit (runs after the banner has rendered)
  useEffect(() => {
    if (failedSubmits > 0) summaryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [failedSubmits]);

  // Revoke preview urls only when the form unmounts; removePhoto revokes single ones
  const photosRef = useRef(photos);
  photosRef.current = photos;
  useEffect(() => () => photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.preview)), []);

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setPhotoError("");

    const accepted = [];
    for (const file of files) {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      if (VALID_EXTENSIONS.indexOf(ext) === -1) {
        setPhotoError("Photos must be JPG or PNG images.");
        continue;
      }
      if (file.size > MAX_PHOTO_BYTES) {
        setPhotoError("Each photo must be 5 MB or smaller.");
        continue;
      }
      accepted.push({ file, preview: URL.createObjectURL(file) });
    }

    let next = photos.concat(accepted);
    if (next.length > maxPhotos) {
      setPhotoError(`You can upload up to ${maxPhotos} photos.`);
      next.slice(maxPhotos).forEach((photo) => URL.revokeObjectURL(photo.preview));
      next = next.slice(0, maxPhotos);
    }
    setPhotos(next);
    if (next.length >= minPhotos) setErrors((prev) => ({ ...prev, photos: undefined }));
  };

  const removePhoto = (index) => {
    URL.revokeObjectURL(photos[index].preview);
    setPhotos(photos.filter((photo, i) => i !== index));
  };

  const validate = () => {
    const next = {};
    const makeValue = make.trim();
    const preset = COLORS.find((item) => isColor(color, item.id));
    const colorValue = preset ? preset.id : color.trim();

    if (license.trim() === "") next.license = "Enter the vehicle number";
    else if (license.trim().length > 20) next.license = "Maximum 20 characters";
    if (makeValue === "") next.make = "Enter the make";
    if (model.trim() === "") next.model = "Enter the model";
    if (colorValue === "") next.color = "Choose or enter your vehicle's color";

    const needed = requirePhotos || photos.length > 0 ? minPhotos : 0;
    if (photos.length < needed) {
      const missing = needed - photos.length;
      next.photos = photos.length === 0 ? `Add at least ${minPhotos} photos` : `Add ${missing} more photo${missing > 1 ? "s" : ""}`;
    }
    return { next, values: { license: license.trim(), make: makeValue, model: model.trim(), color: colorValue } };
  };

  const submit = (e) => {
    e.preventDefault();
    const { next, values } = validate();
    const clean = Object.keys(next).reduce((acc, key) => (next[key] ? { ...acc, [key]: next[key] } : acc), {});
    setErrors(clean);
    if (Object.keys(clean).length > 0) {
      setFailedSubmits((n) => n + 1);
      return;
    }
    return onSubmit(values, photos.map((photo) => photo.file));
  };

  const errorList = Object.values(errors).filter(Boolean);
  const emptySlots = Math.max(0, minPhotos - photos.length);

  return (<form onSubmit={submit} className={compact ? `${styles.form} ${styles.compactForm}` : styles.form} noValidate>
    {errorList.length > 0 && (<div ref={summaryRef} className={styles.errorSummary} role="alert">
      <strong>{errorList.length} field{errorList.length > 1 ? "s need" : " needs"} attention</strong>
      <span>{errorList.join(" · ")}</span>
    </div>)}

    <p className={styles.muted}>All fields marked * are required.</p>

    <section className={styles.card}>
      <p className={styles.sectionLabel}>VEHICLE DETAILS</p>

      <div className={styles.field}>
        <label htmlFor="vd-license" className={styles.label}>Vehicle number*</label>
        <input id="vd-license" className={`${styles.input} ${styles.plateInput} ${errors.license ? styles.inputError : ""}`} value={license} maxLength={20} disabled={submitting} onChange={(e) => setLicense(e.target.value.toUpperCase())} placeholder="ABC 1234" autoComplete="off" />
        <span className={errors.license ? styles.fieldError : styles.hint}>{errors.license || "As shown on your license plate"}</span>
      </div>

      <div className={styles.field}>
        <label htmlFor="vd-make" className={styles.label}>Make*</label>
        <input id="vd-make" className={`${styles.input} ${errors.make ? styles.inputError : ""}`} value={make} maxLength={50} disabled={submitting} onChange={(e) => setMake(e.target.value)} placeholder="e.g. Toyota" />
        {errors.make && <span className={styles.fieldError}>{errors.make}</span>}
      </div>

      <div className={styles.field}>
        <label htmlFor="vd-model" className={styles.label}>Model*</label>
        <input id="vd-model" className={`${styles.input} ${errors.model ? styles.inputError : ""}`} value={model} maxLength={50} disabled={submitting} onChange={(e) => setModel(e.target.value)} placeholder="e.g. Camry" />
        {errors.model && <span className={styles.fieldError}>{errors.model}</span>}
      </div>

      <div className={styles.field}>
        <span id="vd-color-label" className={styles.label}>Color*</span>
        <div role="radiogroup" aria-labelledby="vd-color-label" className={`${styles.colorGrid} ${errors.color ? styles.colorGridError : ""}`}>
          {COLORS.map((item) => (<button
            type="button"
            role="radio"
            aria-checked={isColor(color, item.id)}
            key={item.id}
            className={isColor(color, item.id) ? `${styles.colorTile} ${styles.colorTileOn}` : styles.colorTile}
            disabled={submitting}
            onClick={() => { setColor(item.id); setErrors((prev) => ({ ...prev, color: undefined })); }}
          >
            <span className={styles.colorDot} style={{ background: item.hex }} />
            {item.id}
          </button>))}
        </div>
        <input aria-label="Color" className={`${styles.input} ${errors.color ? styles.inputError : ""}`} value={color} maxLength={30} disabled={submitting} onChange={(e) => setColor(e.target.value)} placeholder="Or type a color, e.g. Dark blue" />
        {errors.color && <span className={styles.fieldError}>{errors.color}</span>}
      </div>
    </section>

    <section className={styles.card}>
      <div className={styles.photoHead}>
        <p className={styles.sectionLabel}>PHOTOS{requirePhotos ? "*" : ""}</p>
        <span className={styles.muted}>{photos.length} of {maxPhotos}</span>
      </div>
      <p className={errors.photos ? styles.fieldError : styles.hint}>
        {errors.photos
          ? `${errors.photos}. Include the front and the rear with the plate clearly visible.`
          : requirePhotos
            ? `Add at least ${minPhotos} photos: include the front and the rear with the plate clearly visible.`
            : `Optional. New photos replace the current ones (at least ${minPhotos}: the front and the rear with the plate).`}
      </p>

      <div className={styles.photoGrid}>
        {photos.map((photo, index) => (<div key={photo.preview} className={styles.photoThumb}>
          <img src={photo.preview} alt={SLOT_LABELS[index] || `Vehicle photo ${index + 1}`} />
          {SLOT_LABELS[index] && <span className={styles.photoTag}>{SLOT_LABELS[index]}</span>}
          <button type="button" aria-label="Remove photo" onClick={() => removePhoto(index)} disabled={submitting}>×</button>
        </div>))}
        {photos.length < maxPhotos && (<label className={styles.photoAdd}>
          <Plus />
          <span>{SLOT_LABELS[photos.length] || "Add photo"}</span>
          <input type="file" accept="image/jpeg,image/png" multiple onChange={addPhotos} disabled={submitting} />
        </label>)}
        {Array.from({ length: Math.max(0, emptySlots - 1) }).map((x, i) => (<div key={"slot" + i} className={styles.photoSlot}>{SLOT_LABELS[photos.length + 1 + i] || ""}</div>))}
      </div>

      {photos.length < maxPhotos && (<div className={styles.photoButtons}>
        <label className={styles.secondaryButton}>
          <CameraIcon /> Take photo
          <input type="file" accept="image/jpeg,image/png" capture="environment" onChange={addPhotos} disabled={submitting} />
        </label>
        <label className={styles.secondaryButton}>
          Gallery
          <input type="file" accept="image/jpeg,image/png" multiple onChange={addPhotos} disabled={submitting} />
        </label>
      </div>)}
      <p className={styles.hint}>JPG or PNG · up to {maxPhotos} photos · 5 MB each</p>
      {photoError !== "" && <p className={styles.fieldError}>{photoError}</p>}
    </section>

    <div className={styles.actions}>
      {onCancel && <button type="button" className={styles.secondaryButton} onClick={onCancel} disabled={submitting}>Cancel</button>}
      <button type="submit" className={styles.primaryButton} disabled={submitting}>{submitting ? "PLEASE WAIT..." : submitLabel}</button>
    </div>
  </form>);
};

export default VehicleDetailsForm;
